import * as yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import RuleItem from 'components/rule-item';
import apiService from 'library/api-service';
import { passwordRules } from 'components/rule-item';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import LoadingBackdrop from 'components/loading-backdrop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ResponsiveDialog, { ResponsiveDialogActions, ResponsiveDialogContent } from 'components/responsive-dialog';
import { Box, Button, TextField, FormLabel, IconButton, DialogTitle, FormControl, InputAdornment } from '@mui/material';

export type ChangePasswordFormParams = {
  newPassword: string;
  currentPassword: string;
  newPasswordRetry: string;
};

export type ChangePasswordPostParams = {
  oldPassword: string;
  newPassword: string;
};

type ChangePasswordDialogProps = {
  open: boolean;
  handleClose: () => void;
};

const ChangePasswordDialog = ({ open, handleClose: closeDialog }: ChangePasswordDialogProps) => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPasswordRetry, setShowNewPasswordRetry] = useState(false);

  // form validations & form state
  const schema = yup.object({
    currentPassword: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    newPassword: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .matches(
        /^(?!.*(?:[iI][dD][eE][cC][oO][nN]|[eE][hH][eE][sS][aA][pP][pP]))(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
        intl.formatMessage({
          id: 'validations.invalidPasswordFormat',
          defaultMessage: 'Geçersiz parola formatı',
        })
      )
      .notOneOf(
        [yup.ref('currentPassword')],
        intl.formatMessage({
          id: 'validations.samePassword',
          defaultMessage: 'Yeni parola, mevcut paroladan farklı olmalı',
        })
      ),
    newPasswordRetry: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .oneOf(
        [yup.ref('newPassword')],
        intl.formatMessage({ id: 'validations.missMatchPassword', defaultMessage: 'Parola eşleşmedi' })
      ),
  });

  const {
    reset,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormParams>({
    defaultValues: { currentPassword: '', newPassword: '', newPasswordRetry: '' },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  // dynamic variables
  const newPasswordValue = watch('newPassword');

  // async helper functions
  const onSubmit = async (data: ChangePasswordFormParams) => {
    try {
      setLoading(true);

      const postData: ChangePasswordPostParams = {
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      };

      const response = await apiService.changePassword(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage id="profile.changePasswordCompleted" defaultMessage="Parola başarıyla değiştirildi." />,
          { variant: 'success' }
        );

        handleClose();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
  const handleClickShowNewPasswordRetry = () => setShowNewPasswordRetry(!showNewPasswordRetry);

  const handleClose = () => {
    closeDialog();
    reset();
    setShowNewPassword(false);
    setShowCurrentPassword(false);
    setShowNewPasswordRetry(false);
  };

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="profile.changePassword" defaultMessage="Parola Değiştir" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ width: '100%', minWidth: 0 }}>
        <ResponsiveDialogContent>
          <FormControl>
            <FormLabel htmlFor="currentPassword">
              <FormattedMessage id="profile.currentPassword" defaultMessage="Mevcut Parola" />
            </FormLabel>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  id="password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="******"
                  autoComplete="currentPassword"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            sx={{ border: 'none' }}
                            aria-label="toggle password visibility"
                            onClick={handleClickShowCurrentPassword}>
                            {showCurrentPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="newPassword">
              <FormattedMessage id="profile.newPassword" defaultMessage="Yeni Parola" />
            </FormLabel>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="******"
                  autoComplete="newPassword"
                  required
                  fullWidth
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            sx={{ border: 'none' }}
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPassword}>
                            {showNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="newPasswordRetry">
              <FormattedMessage id="profile.newPasswordRetry" defaultMessage="Yeni Parola Tekrar" />
            </FormLabel>
            <Controller
              name="newPasswordRetry"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.newPasswordRetry}
                  helperText={errors.newPasswordRetry?.message}
                  id="newPasswordRetry"
                  type={showNewPasswordRetry ? 'text' : 'password'}
                  placeholder="******"
                  autoComplete="newPasswordRetry"
                  required
                  fullWidth
                  variant="outlined"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            sx={{ border: 'none' }}
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPasswordRetry}>
                            {showNewPasswordRetry ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <RuleItem
              ok={passwordRules.minLength(newPasswordValue)}
              text={intl.formatMessage({ id: 'passwordRules.minLength', defaultMessage: 'En az 8 karakter' })}
            />
            <RuleItem
              ok={passwordRules.hasUpper(newPasswordValue)}
              text={intl.formatMessage({ id: 'passwordRules.hasUpper', defaultMessage: 'En az 1 büyük harf' })}
            />
            <RuleItem
              ok={passwordRules.hasLower(newPasswordValue)}
              text={intl.formatMessage({ id: 'passwordRules.hasLower', defaultMessage: 'En az 1 küçük harf' })}
            />
            <RuleItem
              ok={passwordRules.hasNumber(newPasswordValue)}
              text={intl.formatMessage({ id: 'passwordRules.hasNumber', defaultMessage: 'En az 1 rakam' })}
            />
            <RuleItem
              ok={passwordRules.hasSymbol(newPasswordValue)}
              text={intl.formatMessage({ id: 'passwordRules.hasSymbol', defaultMessage: 'En az 1 özel karakter' })}
            />
            <RuleItem
              ok={passwordRules.noForbiddenWords(newPasswordValue)}
              text={intl.formatMessage({
                id: 'passwordRules.noForbiddenWords',
                defaultMessage: 'Idecon / eHesApp içermemeli',
              })}
            />
          </Box>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default ChangePasswordDialog;
